import { WORK_PERMIT_STATUS } from '../consts'
import { WP_REVIEWER_HISTORY_STATUSES } from './contracts'

export const WP_ACTIVE_REVIEW_STATUSES = [
  WORK_PERMIT_STATUS.ACKNOWLEDGED,
  WORK_PERMIT_STATUS.REVIEWED,
  WORK_PERMIT_STATUS.REVIEWED2,
]

export const WP_EXTERNAL_VIEW_COMMENT_STATUSES = [
  WORK_PERMIT_STATUS.APPR,
  WORK_PERMIT_STATUS.REJECTED,
  WORK_PERMIT_STATUS.SUSPENDED,
  WORK_PERMIT_STATUS.COMPLETED,
  WORK_PERMIT_STATUS.EXPIRED,
  WORK_PERMIT_STATUS.ENDED,
]

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Check whether a Work Permit status is an active reviewer status.
 * @param {string} status
 * @returns {boolean}
 */
export function isActiveReviewStatus (status) {
  return WP_ACTIVE_REVIEW_STATUSES.includes(status)
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Check whether external users can see the collapse history comment section.
 * @param {string} currentStatus
 * @returns {boolean}
 */
export function canExternalViewerSeeHistorySection (currentStatus) {
  return WP_EXTERNAL_VIEW_COMMENT_STATUSES.includes(currentStatus)
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Check whether external users can see one Lifecycle history row.
 * @param {string} rowStatus
 * @returns {boolean}
 */
export function canExternalViewerSeeLifecycleHistoryRow (rowStatus) {
  return !isActiveReviewStatus(rowStatus)
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Check whether an engineer row can show reviewer external disclosure controls.
 * @param {Object} params
 * @param {boolean} params.isEngineer
 * @param {boolean} params.approverSecurity
 * @param {string} params.currentStatus
 * @param {string} params.rowStatus
 * @returns {boolean}
 */
export function canShowEngineerHistoryDisclosure ({
  isEngineer,
  approverSecurity,
  currentStatus,
  rowStatus,
} = {}) {
  return !!isEngineer &&
    !!approverSecurity &&
    isActiveReviewStatus(currentStatus) &&
    rowStatus !== WORK_PERMIT_STATUS.REJECTED
}

/**
 * author: alvis
 * timestamp: 2026-05-11
 * description: Whether engineer collapse history should render the read-only disclosure indicator (disclosure grid).
 * Editable checkbox remains `canShowEngineerHistoryDisclosure` (approver + active review). Omit when the history
 * row is REJECTED (reject comment body only). Omit when the permit's current status is REJECTED. Omit whenever the
 * permit's current status is an active review status (`WP_ACTIVE_REVIEW_STATUSES`) so the indicator never shows
 * during ACK / REVIEWED / REVIEWED2; after approval (e.g. APPR) reviewer rows may still show the read-only indicator.
 * @param {Object} params
 * @param {string} params.currentPermitStatus
 * @param {string} params.rowStatus
 * @returns {boolean}
 */
export function shouldShowEngineerHistoryDisclosureIndicator ({
  currentPermitStatus,
  rowStatus,
} = {}) {
  if (rowStatus === WORK_PERMIT_STATUS.REJECTED) return false
  if (currentPermitStatus === WORK_PERMIT_STATUS.REJECTED) return false
  const permitAllowsExternalCommentContext =
    canExternalViewerSeeHistorySection(currentPermitStatus) ||
    isActiveReviewStatus(currentPermitStatus)
  if (!permitAllowsExternalCommentContext) return false
  if (isActiveReviewStatus(currentPermitStatus)) return false
  return WP_REVIEWER_HISTORY_STATUSES.includes(rowStatus)
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Check whether the hoisting history block is visible for the current viewer.
 * @returns {boolean}
 */
export function canExternalViewerSeeHoistingHistory () {
  return true
}
